import type { ConsentType, CustomerAddress, CustomerDetail } from '@/api/types'

import { DEFAULT_NATIONALITY } from './constants'
import type { CustomerWizardInput } from './validation'

/**
 * Turning a customer record back into the form that captured it.
 *
 * The edit screen sends the whole `CustomerWriteRequest`, because that is what the endpoint takes —
 * a KYC record edited a piece at a time is one that can sit half-valid between calls. So every
 * field has to come back out of the record intact, including the ones nobody is going to touch.
 *
 * That is also why the screen refuses without `customers.pii.view`: the Ghana Card number and the
 * phone arrive masked without it, and the date of birth does not arrive at all. Seeding a form from
 * masked values and then submitting it would write the mask into the record.
 */

const blankAddress = {
  ghanaPostGps: '',
  streetOrArea: '',
  landmark: '',
  town: '',
  district: '',
  region: '',
}

const fromAddress = (address: CustomerAddress | undefined) =>
  address
    ? {
        // Empty string rather than null throughout: these back controlled inputs, and React
        // treats a null value as an uncontrolled field. What the record is missing, the form shows
        // as blank and the officer fills in.
        ghanaPostGps: address.ghanaPostGps ?? '',
        streetOrArea: address.streetOrArea ?? '',
        landmark: address.landmark ?? '',
        town: address.town ?? '',
        district: address.district ?? '',
        region: address.region ?? '',
      }
    : blankAddress

export function toCustomerWizardValues(customer: CustomerDetail): CustomerWizardInput {
  // The current residence, or the most recent one recorded. A record with no residence address
  // cannot have left draft, so the empty case is a record still in capture.
  const residence =
    customer.addresses.find((address) => address.addressType === 'Residence' && address.isCurrent) ??
    customer.addresses.find((address) => address.addressType === 'Residence')

  const business =
    customer.addresses.find((address) => address.addressType === 'Business' && address.isCurrent) ??
    customer.addresses.find((address) => address.addressType === 'Business')

  const ghanaCard =
    customer.identityDocuments.find((document) => document.idType === 'GhanaCard' && document.isPrimary) ??
    customer.identityDocuments.find((document) => document.idType === 'GhanaCard')

  const consent = (type: ConsentType) =>
    customer.consents.find((entry) => entry.consentType === type)?.granted ?? false

  return {
    branchId: customer.branchId,

    firstName: customer.firstName,
    middleName: customer.middleName ?? '',
    lastName: customer.lastName,
    gender: customer.gender,
    // Absent rather than empty when the caller cannot see it. The screen refuses in that case, so
    // reaching here with an empty string means the record genuinely has none.
    dateOfBirth: customer.dateOfBirth ?? '',
    placeOfBirth: customer.placeOfBirth ?? '',
    nationality: customer.nationality || DEFAULT_NATIONALITY,
    maritalStatus: customer.maritalStatus,

    ghanaCardNumber: customer.ghanaCardNumber ?? '',
    ghanaCardIssueDate: ghanaCard?.issueDate ?? '',
    ghanaCardExpiryDate: ghanaCard?.expiryDate ?? '',

    phoneNumber: customer.phoneNumber,
    altPhoneNumber: customer.altPhoneNumber ?? '',
    email: customer.email ?? '',

    occupation: customer.occupation ?? '',
    employerOrBusinessName: customer.employerOrBusinessName ?? '',
    monthlyIncomeEstimateMinor: customer.monthlyIncomeEstimateMinor ?? 0,

    residence: fromAddress(residence),
    hasBusinessAddress: business !== undefined,
    business: fromAddress(business),

    // A record with no next of kin gets one empty row to fill in, exactly as capture does. A blank
    // row is dropped on submission, so offering it costs nothing.
    nextOfKin:
      customer.nextOfKin.length > 0
        ? customer.nextOfKin.map((kin) => ({
            fullName: kin.fullName,
            relationship: kin.relationship,
            phoneNumber: kin.phoneNumber,
            address: kin.address ?? '',
          }))
        : [{ fullName: '', relationship: '', phoneNumber: '', address: '' }],

    consentAccountOperation: consent('AccountOperation'),
    consentCreditBureau: consent('CreditBureau'),
    consentMarketing: consent('Marketing'),
    consentBiometricCapture: consent('BiometricCapture'),
  }
}

/** Ignores the punctuation and case a card number can be typed with. */
const compareCard = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '')

/**
 * Whether this amendment is one that returns an approved file to `PendingReview`.
 *
 * An approval is given for a named person evidenced by a document. Change the evidence — the Ghana
 * Card number or the date of birth — and the approval no longer describes the file, so the server
 * sends it back for review. Amending a phone number or an occupation does not.
 *
 * The comparison happens here rather than in the component so that the warning shown while typing
 * and the confirmation shown at the click are the same question, answered once.
 */
export function amendmentResetsKyc(
  original: CustomerDetail,
  values: Pick<CustomerWizardInput, 'ghanaCardNumber' | 'dateOfBirth'>,
): { ghanaCard: boolean; dateOfBirth: boolean; any: boolean } {
  const ghanaCard = compareCard(values.ghanaCardNumber) !== compareCard(original.ghanaCardNumber ?? '')
  const dateOfBirth = values.dateOfBirth !== (original.dateOfBirth ?? '')

  return { ghanaCard, dateOfBirth, any: ghanaCard || dateOfBirth }
}
